import type React from 'react'
import PersonalDetailItem from '../../../../../../components/PersonalDetailItem'
import type { AdminWealthManagerDto } from '../../../../../../redux/services/coreApi'
import type { WealthManagerActivation } from '../../../../../../types/coreApi-types'
import { RiskRatingBadge, WmStatusBadge } from './WealthManagerChips'

interface WealthManagerOverviewProps {
  wealthManager: AdminWealthManagerDto
  book?: WealthManagerActivation
  onInvite: () => void
  onMarkAgreementSigned: () => void
  onSetRiskRating: () => void
}

// A compact inline action button — sits on the right of the detail row whose value it changes, so the
// control is next to what it acts on rather than buried in a page-header corner.
const RowAction: React.FC<{ onClick: () => void; children: React.ReactNode }> = ({
  onClick,
  children,
}) => (
  <button
    type="button"
    onClick={onClick}
    className="shrink-0 rounded-lg border border-solid border-brand-light-card-border px-3 py-1 text-xs font-semi text-brand-light-text-default hover:bg-black/5 transition-colors"
  >
    {children}
  </button>
)

// A labelled row: fixed-width label, a value (text or a badge), and an optional action pinned to the
// right. Same column layout as PersonalDetailItem, which only takes plain-string values.
const DetailRow: React.FC<{
  title: string
  children: React.ReactNode
  action?: React.ReactNode
}> = ({ title, children, action }) => (
  <div className="flex flex-col md:flex-row md:items-center gap-2">
    <p className="text-description mb-0.5 md:mb-0 md:w-[180px]">{title}</p>
    <div className="flex-1">{children}</div>
    {action && <div className="shrink-0">{action}</div>}
  </div>
)

// The wealth manager's identity and lifecycle at a glance: who they are, where they sit on the
// Seeded → Invited → Active ladder, their admin-attested risk rating, and a one-line activation summary
// of their book. Each lifecycle action sits inline next to the field it changes — Invite by the status,
// "Mark agreement signed" by the agreement, "Set risk rating" by the rating.
const WealthManagerOverview: React.FC<WealthManagerOverviewProps> = ({
  wealthManager,
  book,
  onInvite,
  onMarkAgreementSigned,
  onSetRiskRating,
}) => {
  const agreementSignedAt = wealthManager.agreementSignedAt
    ? new Date(wealthManager.agreementSignedAt).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      })
    : 'Not signed'

  return (
    <div className="space-y-3">
      <h1 className="h1">Overview</h1>

      <div className="card gap-5">
        <DetailRow
          title="Status"
          action={
            wealthManager.status === 'Seeded' ? (
              <RowAction onClick={onInvite}>Invite</RowAction>
            ) : undefined
          }
        >
          <WmStatusBadge
            status={wealthManager.status}
            isInvited={wealthManager.isInvited}
          />
        </DetailRow>

        <DetailRow
          title="Risk rating"
          action={
            <RowAction onClick={onSetRiskRating}>
              {wealthManager.riskRating ? 'Change' : 'Set rating'}
            </RowAction>
          }
        >
          <RiskRatingBadge riskRating={wealthManager.riskRating} />
        </DetailRow>

        <DetailRow
          title="Agreement signed"
          action={
            wealthManager.status !== 'Active' ? (
              <RowAction onClick={onMarkAgreementSigned}>
                Mark signed
              </RowAction>
            ) : undefined
          }
        >
          <p className="text-default-small">{agreementSignedAt}</p>
        </DetailRow>

        <PersonalDetailItem title="Email" value={wealthManager.email} />
        <PersonalDetailItem title="Phone" value={wealthManager.phone} />
        <PersonalDetailItem
          title="Veli advisor id"
          value={wealthManager.veliAdvisorId}
        />
      </div>

      <div className="card gap-2">
        <h2 className="font-semi color-text-default">Book</h2>
        {book ? (
          <p className="text-sm color-text-description">
            {book.activated} of {book.total} client
            {book.total === 1 ? '' : 's'} activated · {book.notActivated} not yet
          </p>
        ) : (
          <p className="text-sm color-text-description">
            {wealthManager.clientCount} client
            {wealthManager.clientCount === 1 ? '' : 's'} in this book.
          </p>
        )}
      </div>
    </div>
  )
}

export default WealthManagerOverview
